import { useAuth } from '@/app/AuthContext.jsx';
import { homeForCapability, SUPPORT_EMAIL } from '@/app/constants.js';
import { TurnstileComponent } from '@/lib';
import { resendActivationRequest } from '@/lib/recepthor-api';
import { Button, Input } from '@/ui';
import { EyeIcon, EyeOffIcon } from '@/ui/icons';
import { useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import BrandPane from './BrandPane.jsx';
import './login.css';

// El selector de arriba del formulario: un login por portal (REC-1930).
const PORTALS = [
  { receptor: false, label: 'Abogado', to: '/login' },
  { receptor: true, label: 'Receptor', to: '/login?portal=receptor' },
];

function receptorMembership(memberships) {
  return memberships.find((m) => m.capabilities?.[0] === 'receptor');
}

function homeMembership(memberships) {
  // Only orgs whose capability has a home — matches AuthContext layout picks.
  // An owned external_service (etc.) must not block a later firm/receptor/backoffice.
  // Product lock: only capabilities[0]. Never scan the array. Never memberships[0].
  const withHome = memberships.filter((m) =>
    homeForCapability(m.capabilities?.[0]),
  );
  if (!withHome.length) return null;
  return withHome.find((m) => m.isOwner) ?? withHome[0];
}

export default function Login() {
  const { login, selectOrganization } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  // Cloudflare escaló a desafío interactivo (checkbox): el botón debe dirigir
  // al usuario al widget, no decir "verificando" como si fuera automático.
  const [captchaInteractive, setCaptchaInteractive] = useState(false);
  const captchaRef = useRef(null);
  // The two refusals the activation email resolves: an inactive account, and
  // an active one whose user never accepted the terms in force. The activation
  // form is where the acceptance is recorded.
  const [activationOffered, setActivationOffered] = useState(
    Boolean(location.state?.termsPending),
  );
  const [activationSent, setActivationSent] = useState(false);
  const [supportOffered, setSupportOffered] = useState(false);
  // Counts the login submits. A resend that finishes after a newer submit
  // belongs to a screen that is already settled, so it must not write to it.
  const loginAttempt = useRef(0);
  // The email field as it is now: the inputs stay editable while a login is in
  // flight, so a late answer checks it against the email it was sent with.
  const latestEmail = useRef('');
  const [activationLoading, setActivationLoading] = useState(false);

  const sessionExpired = location.state?.sessionExpired;
  const termsPending = location.state?.termsPending;
  const accountActivated = location.state?.accountActivated;
  const passwordReset = location.state?.passwordReset;
  const leftLastOrg = location.state?.leftLastOrg;
  const membershipPending = location.state?.membershipPending;
  const invitationAccepted = location.state?.invitationAccepted;

  const portalReceptor =
    new URLSearchParams(location.search).get('portal') === 'receptor';

  // The support notice and its error are about the account that was refused:
  // another email retires them. The password does not.
  const handleEmailChange = (e) => {
    latestEmail.current = e.target.value;
    setEmail(e.target.value);
    if (supportOffered) {
      setSupportOffered(false);
      setError('');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    loginAttempt.current += 1;
    const submittedEmail = email;
    setLoading(true);
    setError('');
    setSupportOffered(false);
    try {
      const result = await login(email, password, captchaToken);
      const memberships = result?.memberships ?? [];
      if (portalReceptor) {
        const receptor = receptorMembership(memberships);
        if (!receptor?.organizationId) {
          setError('No hay un espacio al que entrar con esta cuenta.');
          return;
        }
        selectOrganization?.(receptor.organizationId);
        navigate(homeForCapability('receptor'));
        return;
      }
      const active = homeMembership(memberships);
      const cap = active?.capabilities?.[0];
      if (!cap) {
        setError('No hay un espacio al que entrar con esta cuenta.');
        return;
      }
      const dest = homeForCapability(cap);
      if (!dest) {
        setError('No hay un espacio al que entrar con esta cuenta.');
        return;
      }
      navigate(dest);
    } catch (error) {
      const credsFail =
        error.message === 'No autorizado. Credenciales incorrectas.';
      // The refusal names the account that was sent. If the field holds
      // another email by now, it says nothing about the one on screen.
      const staleInactive =
        error.code === 'UserNotActive' &&
        latestEmail.current !== submittedEmail;
      if (!staleInactive) {
        setError(
          portalReceptor && credsFail
            ? 'No se pudo entrar. Correo o contraseña incorrectos. Revisa e intenta de nuevo.'
            : error.message,
        );
        setActivationOffered(error.code === 'TermsNotAccepted');
        setSupportOffered(error.code === 'UserNotActive');
        setActivationSent(false);
      }
      // El token se consumió con el intento fallido (es de un solo uso):
      // resetear el widget emite uno fresco para el reintento.
      setCaptchaToken('');
      captchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const handleResendActivation = async () => {
    const attempt = loginAttempt.current;
    const superseded = () => loginAttempt.current !== attempt;
    setActivationLoading(true);
    setError('');
    try {
      const response = await resendActivationRequest({ email });
      if (!response.ok) throw new Error('resend failed');
      if (!superseded()) setActivationSent(true);
    } catch {
      if (!superseded()) {
        setError('No pudimos enviar el correo. Inténtalo de nuevo.');
      }
    } finally {
      setActivationLoading(false);
    }
  };

  const handleCaptchaVerify = (token) => {
    setCaptchaToken(token);
    if (token) setCaptchaInteractive(false);
  };

  return (
    <div className="login-page">
      <BrandPane />

      <div className="login-form-pane">
        <form className="login-form" onSubmit={handleLogin}>
          {/* Cambiar de portal solo cambia la query: LoginRoute mantiene este
              formulario montado y lo escrito se conserva. Mientras un login
              está en curso el otro portal no es un enlace, porque la
              respuesta llevaría al destino del portal anterior. */}
          <nav className="login-portal" aria-label="Portal de acceso">
            {PORTALS.map(({ receptor, label, to }) => {
              const active = receptor === portalReceptor;
              if (!active && loading) {
                return (
                  <span
                    key={label}
                    role="link"
                    className="login-portal__tab"
                    aria-disabled="true"
                  >
                    {label}
                  </span>
                );
              }
              return (
                // El aviso de la página (contraseña actualizada, cuenta
                // activada…) y keepForm viajan con el cambio de portal.
                <Link
                  key={label}
                  to={to}
                  replace
                  state={location.state}
                  className="login-portal__tab"
                  aria-current={active ? 'page' : undefined}
                >
                  {label}
                </Link>
              );
            })}
          </nav>
          <p className="login-eyebrow">
            {portalReceptor ? 'Portal del receptor' : 'Acceso al sistema'}
          </p>
          <h1 className="login-heading">Inicia sesión</h1>

          <div className="login-fields">
            <div className="login-field">
              <label className="login-label" htmlFor="login-email">
                Correo electrónico *
              </label>
              <Input
                id="login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={handleEmailChange}
                required
              />
            </div>

            <div className="login-field">
              <label className="login-label" htmlFor="login-password">
                Contraseña *
              </label>
              <div className="login-password-wrapper">
                <Input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Ocultar' : 'Mostrar'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            <div className="login-captcha">
              <TurnstileComponent
                ref={captchaRef}
                onVerify={handleCaptchaVerify}
                onInteractive={() => setCaptchaInteractive(true)}
              />
            </div>

            {sessionExpired && (
              <p className="login-alert login-alert--info">
                Tu sesión ha expirado. Inicia sesión nuevamente.
              </p>
            )}
            {termsPending && !error && (
              <p className="login-alert login-alert--info">
                Para seguir usando Recepthor debes aceptar los términos y
                condiciones vigentes.
              </p>
            )}
            {accountActivated && (
              <p className="login-alert login-alert--ok">
                Cuenta activada exitosamente. Inicia sesión con tu nueva
                contraseña.
              </p>
            )}
            {passwordReset && (
              <p className="login-alert login-alert--ok">
                Contraseña actualizada exitosamente. Inicia sesión con tu nueva
                contraseña.
              </p>
            )}
            {invitationAccepted && (
              <p className="login-alert login-alert--ok">
                Invitación aceptada. Inicia sesión para continuar.
              </p>
            )}
            {leftLastOrg && (
              <p className="login-alert login-alert--info">
                Saliste de la organización.
              </p>
            )}
            {membershipPending && (
              <p className="login-alert login-alert--info">
                Tienes una invitación pendiente. Acéptala o recházala para
                continuar.
              </p>
            )}
            {error && (
              <p className="login-alert login-alert--err" role="alert">
                {error}
              </p>
            )}
            {supportOffered && (
              <p className="login-alert login-alert--info" role="alert">
                Escríbenos a{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> para
                activarla.
              </p>
            )}

            {activationOffered && !activationSent && (
              <div className="login-activation">
                <p className="login-alert login-alert--info">
                  Te enviaremos un correo con un enlace. Desde ese enlace
                  aceptas los términos y condiciones y defines tu contraseña.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  disabled={activationLoading || !email}
                  onClick={handleResendActivation}
                  className="login-submit"
                >
                  {activationLoading
                    ? 'Enviando…'
                    : 'Enviar correo de activación'}
                </Button>
              </div>
            )}
            {activationSent && (
              <p className="login-alert login-alert--ok">
                Si la cuenta existe, te enviamos un correo con el enlace de
                activación. Revisa tu bandeja de entrada.
              </p>
            )}

            {/* Tras un intento fallido el token se renueva SOLO (reset() →
                Turnstile re-ejecuta el desafío y re-emite en ~1-2 s): el botón
                se rehabilita sin acción del usuario y los campos quedan
                intactos. El label señala esa ventana breve. */}
            <Button
              type="submit"
              variant="seal"
              size="lg"
              disabled={loading || !captchaToken}
              title={
                !captchaToken && !loading ? 'Verificando seguridad…' : undefined
              }
              className="login-submit"
            >
              {loading
                ? 'Iniciando…'
                : !captchaToken
                  ? captchaInteractive
                    ? 'Completa la verificación de arriba'
                    : 'Verificando seguridad…'
                  : 'Iniciar Sesión'}
            </Button>
          </div>

          <Link
            to={
              portalReceptor
                ? '/recuperar-contrasena?portal=receptor'
                : '/recuperar-contrasena'
            }
            className="login-forgot"
          >
            Olvidé mi contraseña
          </Link>
        </form>
      </div>
    </div>
  );
}
